// Form + step navigation state for the New Incident Wizard.
// Owns: form fields, validation errors, step index, completed map, prefill mapping.
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePlants } from "../../api/sitroom";

export const INITIAL_FORM = {
  template: "",
  severity: "",
  title: "",
  description: "",
  parts: [],
  line: "",
  units: "",
  hours: "",
  loss: "",
  internalRef: "",
  files: [],
  additionalParties: [],
  notifyNow: true,
  serviceRequests: [],
};

const CAT_TO_KEY = {
  jis_sequence_error: "jis",
  line_stoppage: "stoppage",
  quality_lock: "quality",
  inbound_transport_delay: "transport",
  packaging_shortage: "packaging",
  customs_border_hold: "customs",
  warehouse_damage: "damage",
  raw_material_shortage: "material",
};

export const useWizardState = () => {
  const location = useLocation();

  const initialForm = useMemo(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return INITIAL_FORM;
    return {
      ...INITIAL_FORM,
      template: CAT_TO_KEY[prefill.category] || "",
      severity: prefill.severity || "",
      title: prefill.title || "",
      description: prefill.description || "",
      line: prefill.affected_line || "",
      units: prefill.estimated_units_impact ? String(prefill.estimated_units_impact) : "",
      hours: prefill.estimated_hours_impact ? String(prefill.estimated_hours_impact) : "",
      loss: prefill.estimated_eur_loss ? String(prefill.estimated_eur_loss) : "",
      parts: (prefill.affected_part_numbers || []).map((p) => ({ part_number: p })),
      additionalParties: (prefill.suggested_parties || []).map((name) => ({
        name, role: "AI-suggested", type: "Tier 1",
      })),
    };
  }, [location.state]);

  const [step, setStep] = useState(initialForm.template ? 2 : 1);
  const [completed, setCompleted] = useState(initialForm.template ? { 1: true } : {});
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  // Upgrade bare "Line N" prefill to "<plant> · Line N" once plants load
  const { data: plantsList = [] } = usePlants();
  useEffect(() => {
    if (!form.line || !plantsList.length) return;
    if (plantsList.some((p) => `${p.plant} · ${p.line}` === form.line)) return;
    const bareMatch = plantsList.find((p) => p.line.toLowerCase() === form.line.toLowerCase());
    if (bareMatch) {
      setForm((f) => ({ ...f, line: `${bareMatch.plant} · ${bareMatch.line}` }));
    }
  }, [plantsList, form.line]);

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!form.template) errs.template = "Choose a template";
      if (!form.severity) errs.severity = "Choose a severity";
    }
    if (s === 2) {
      if (form.title.trim().length < 10) errs.title = "Min 10 characters";
      if (form.description.trim().length < 30) errs.description = "Min 30 characters";
      if (!form.line) errs.line = "Pick a line / area";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setCompleted((c) => ({ ...c, [step]: true }));
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));
  const goTo = (n) => setStep(n);

  const canNextStep1 = form.template && form.severity;
  const canNextStep2 =
    form.title.trim().length >= 10 && form.description.trim().length >= 30 && Boolean(form.line);

  return {
    form, setForm,
    step, goTo, goNext, goBack,
    completed,
    errors, validateStep, setErrors,
    canNextStep1, canNextStep2,
  };
};
